"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";

// Hero focal-point garment.
// The PNG's photo background is dissolved into the page using:
//   - a single very soft radial mask (no defined "opaque core" → no border)
//   - a wide, low-opacity accent wash (atmospheric, not a halo ring)
//   - contrast + brightness filter so any residual photo edges read darker
// No inner secondary glow — that was creating a visible dark ring last pass.
export function HeroGarment() {
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Scroll-tied parallax — subtle, capped at 80px.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY * 0.15, 80);
        el.style.setProperty("--parallax-y", `${y}px`);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Cursor-tracking tilt — desktop only (lg breakpoint). Tilts ±2.5°
  // based on horizontal mouse position relative to viewport centre.
  // Drops out gracefully for reduced-motion + touch devices.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const mqDesktop = window.matchMedia("(min-width: 1024px)");
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mqDesktop.matches || mqReduce.matches) return;

    let raf = 0;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = window.innerWidth;
        const norm = (e.clientX / w - 0.5) * 2; // -1 … 1
        const tilt = (norm * 2.5).toFixed(2); // ±2.5°
        const lift = (norm * -6).toFixed(1); // small counter-translateX
        el.style.setProperty("--tilt-y", `${tilt}deg`);
        el.style.setProperty("--tilt-x", `${lift}px`);
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="relative mx-auto w-full max-w-[480px] lg:max-w-[540px] xl:max-w-[640px] lg:-mt-24 xl:-mt-40"
      style={{
        transform:
          "perspective(1400px) translate3d(var(--tilt-x, 0), var(--parallax-y, 0), 0) rotateY(var(--tilt-y, 0))",
        transformStyle: "preserve-3d",
        transition: "transform 280ms ease-out",
        willChange: "transform",
      }}
    >
      {/* Single broad accent wash — very diffuse, low opacity, no defined ring.
          Positioned slightly larger than the image so it just tints the
          surrounding page atmosphere instead of forming a halo. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 -z-10 size-[150%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent opacity-20 blur-[160px]"
      />

      {/* Orbiting accent dots — small, subtle */}
      <span
        aria-hidden="true"
        className="absolute top-[18%] -left-2 orbit-1 size-1.5 rounded-full bg-accent shadow-[0_0_10px_rgba(192,247,72,0.5)]"
      />
      <span
        aria-hidden="true"
        className="absolute top-[58%] -right-2 orbit-2 size-1.5 rounded-full bg-accent shadow-[0_0_10px_rgba(192,247,72,0.5)]"
      />
      <span
        aria-hidden="true"
        className="absolute bottom-[6%] left-[35%] orbit-3 size-1 rounded-full bg-accent shadow-[0_0_8px_rgba(192,247,72,0.4)]"
      />

      {/* Floating image — soft Gaussian-style mask with no defined opaque core,
          so the image dissolves smoothly into the page from edge to centre. */}
      <div className="hero-garment-float">
        <Image
          src="/hero/garment.png"
          alt="3D clothing brand mockup — okpromo hero"
          width={960}
          height={1200}
          priority
          sizes="(max-width: 1024px) 480px, (max-width: 1280px) 540px, 640px"
          className="w-full h-auto"
          style={{
            // Continuous fade from solid centre to fully transparent at the
            // edges — no visible mask shape, no rectangle, no oval.
            maskImage:
              "radial-gradient(ellipse 58% 70% at 50% 48%, black 0%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 58% 70% at 50% 48%, black 0%, transparent 100%)",
            filter:
              "contrast(1.08) brightness(0.82) saturate(0.9) drop-shadow(0 24px 50px rgba(0,0,0,0.55))",
          }}
        />
      </div>
    </div>
  );
}
